import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import api from '../../api';
import logoFull from '../../assets/brand/logo-full-trimmed.png';

const ContractorLoginPage = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const initialTab = new URLSearchParams(location.search).get('tab') === 'register' ? 'register' : 'login';
    const [mode, setMode] = useState(initialTab);

    // ログインフォーム
    const [loginEmail, setLoginEmail] = useState('');
    const [loginStep, setLoginStep] = useState(1); // 1: メール入力, 2: コード入力
    const [loginCode, setLoginCode] = useState('');
    const [loginError, setLoginError] = useState(null);
    const [sending, setSending] = useState(false);
    const [cooldown, setCooldown] = useState(0);

    // 新規登録フォーム
    const [form, setForm] = useState({
        company_name: '', representative: '', phone: '', construction_license_number: '', email: '',
    });
    const [regErrors, setRegErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [regDone, setRegDone] = useState(false);

    const [focusedField, setFocusedField] = useState(null);

    useEffect(() => {
        const tab = new URLSearchParams(location.search).get('tab');
        if (tab === 'register') setMode('register');
    }, [location.search]);

    useEffect(() => {
        if (cooldown <= 0) return;
        const t = setInterval(() => setCooldown(c => c - 1), 1000);
        return () => clearInterval(t);
    }, [cooldown]);

    const inp = (field) => ({
        width: '100%', padding: '10px 14px',
        border: `1px solid ${focusedField === field ? '#005c53' : '#e0e4ea'}`,
        borderRadius: 8, fontSize: '0.9rem', color: '#1a1a2e',
        outline: 'none', transition: 'border-color 0.2s', boxSizing: 'border-box',
        background: '#fff',
    });

    const LBL = { display: 'block', fontSize: '0.85rem', fontWeight: 500, color: '#1a1a2e', marginBottom: 6 };

    // ── OTPログイン ───────────────────────────────────────────────────
    const handleSendOtp = async (e) => {
        e.preventDefault();
        setSending(true);
        setLoginError(null);
        try {
            await api.post('otp/send', { email: loginEmail, user_type: 'contractor' });
            setLoginStep(2);
            setCooldown(60);
        } catch (err) {
            setLoginError(err.response?.data?.detail || 'コードの送信に失敗しました。');
        } finally {
            setSending(false);
        }
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        setLoginError(null);
        try {
            const res = await api.post('otp/verify', { email: loginEmail, code: loginCode, user_type: 'contractor' });
            localStorage.setItem('contractor_access_token', res.data.access);
            localStorage.setItem('contractor_id', res.data.contractor_id);
            localStorage.setItem('company_name', res.data.company_name);
            navigate('/contractor/dashboard');
        } catch (err) {
            setLoginError(err.response?.data?.detail || 'コードが正しくありません。');
            setLoginCode('');
        }
    };

    const handleResendOtp = async () => {
        setSending(true);
        setLoginError(null);
        try {
            await api.post('otp/send', { email: loginEmail, user_type: 'contractor' });
            setCooldown(60);
            setLoginCode('');
        } catch (err) {
            setLoginError(err.response?.data?.detail || 'コードの送信に失敗しました。');
        } finally {
            setSending(false);
        }
    };

    // ── 新規登録 ─────────────────────────────────────────────────────
    const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

    const validateReg = () => {
        const e = {};
        if (!form.company_name.trim()) e.company_name = '必須項目です';
        if (!form.representative.trim()) e.representative = '必須項目です';
        if (!form.phone.trim()) e.phone = '必須項目です';
        if (!form.email.trim()) e.email = '必須項目です';
        return e;
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        const errs = validateReg();
        if (Object.keys(errs).length > 0) { setRegErrors(errs); return; }
        setSubmitting(true);
        setRegErrors({});
        try {
            await api.post('contractors/apply', form);
            setRegDone(true);
        } catch (err) {
            const data = err.response?.data;
            if (data?.errors) {
                const mapped = {};
                Object.entries(data.errors).forEach(([k, msgs]) => { mapped[k] = msgs[0]; });
                setRegErrors(mapped);
            } else {
                setRegErrors({ _global: data?.message || '送信に失敗しました。再度お試しください。' });
            }
        } finally {
            setSubmitting(false);
        }
    };

    const ErrMsg = ({ field }) => regErrors[field]
        ? <p style={{ color: '#e53935', fontSize: '0.78rem', margin: '4px 0 0' }}>{regErrors[field]}</p>
        : null;

    return (
        <div style={{
            minHeight: '80vh',
            background: '#141c3d',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}>
            <style>{`
                @keyframes loginFadeUp {
                    from { opacity: 0; transform: translateY(20px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                @keyframes formSlideIn  { from { opacity:0; transform:translateX(-16px); } to { opacity:1; transform:translateX(0); } }
                @keyframes formSlideInR { from { opacity:0; transform:translateX(16px);  } to { opacity:1; transform:translateX(0); } }
                .login-card-c  { animation: loginFadeUp 0.5s ease both; }
                .form-login-c  { animation: formSlideIn  0.3s ease both; }
                .form-reg-c    { animation: formSlideInR 0.3s ease both; }
                .cont-btn {
                    width:100%; padding:13px;
                    background: #141c3d;
                    color:#fff; border:none; border-radius:8px;
                    font-weight:600; font-size:0.95rem; cursor:pointer;
                    transition: background 0.2s, transform 0.15s;
                    box-shadow: 0 4px 14px rgba(0,0,0,0.2);
                }
                .cont-btn:hover  { background: #1e2a52; transform:translateY(-1px); }
                .cont-btn:active { transform:translateY(0); }
                .cont-btn:disabled { background: #9ca3af; cursor: default; transform: none; }
                @media (max-width: 640px) { .qr-mobile-box-c { display: none !important; } }
            `}</style>

            <div className="login-card-c" style={{
                width: '100%', maxWidth: mode === 'register' ? 480 : 400,
                background: '#fff',
                borderRadius: 16, padding: '36px 32px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                transition: 'max-width 0.3s',
            }}>
                {/* ロゴ */}
                <Link to="/" style={{ display: 'flex', justifyContent: 'center', marginBottom: 4, textDecoration: 'none' }}>
                    <img src={logoFull} alt="のりいれ三丁目" style={{ height: 40, display: 'block' }} />
                </Link>
                <p style={{ textAlign: 'center', fontSize: '0.85rem', color: '#6b7280', marginBottom: 28 }}>業者ログイン</p>

                {/* スマホでログインQR（PC画面でのみ表示） */}
                <div className="qr-mobile-box-c" style={{ display: 'flex', alignItems: 'center', gap: 14, background: '#f5f6f8', borderRadius: 10, padding: '14px 16px', marginBottom: 24 }}>
                    <div style={{ background: '#fff', padding: 6, borderRadius: 6, flexShrink: 0 }}>
                        <QRCodeSVG value={typeof window !== 'undefined' ? window.location.href : ''} size={64} />
                    </div>
                    <p style={{ margin: 0, fontSize: '0.8rem', color: '#6b7280', lineHeight: 1.6 }}>
                        スマホのカメラでQRコードを読み取ると、このログイン画面をスマホで開けます
                    </p>
                </div>

                {/* タブ */}
                <div style={{ display: 'flex', borderBottom: '1px solid #e0e4ea', marginBottom: 28 }}>
                    {[{ key: 'login', label: 'ログイン' }, { key: 'register', label: '新規登録' }].map(({ key, label }) => (
                        <button key={key} onClick={() => { setMode(key); setLoginError(null); setRegErrors({}); setLoginStep(1); setLoginCode(''); }} style={{
                            flex: 1, padding: '10px 0', background: 'none', border: 'none', cursor: 'pointer',
                            fontSize: '0.9rem', fontWeight: mode === key ? 600 : 400,
                            color: mode === key ? '#005c53' : '#6b7280',
                            borderBottom: mode === key ? '2px solid #005c53' : '2px solid transparent',
                            marginBottom: -1, transition: 'all 0.2s',
                        }}>
                            {label}
                        </button>
                    ))}
                </div>

                {/* ── ログインフォーム（OTP） ── */}
                {mode === 'login' && (
                    <div className="form-login-c">
                        {loginStep === 1 ? (
                            <form onSubmit={handleSendOtp}>
                                <div style={{ marginBottom: 20 }}>
                                    <label style={LBL}>メールアドレス</label>
                                    <input type="email" placeholder="example@contractor.jp" value={loginEmail}
                                        onChange={e => setLoginEmail(e.target.value)}
                                        onFocus={() => setFocusedField('email')} onBlur={() => setFocusedField(null)}
                                        style={inp('email')} required />
                                </div>
                                {loginError && <p style={{ color: '#e53935', fontSize: '0.85rem', marginBottom: 12 }}>{loginError}</p>}
                                <button type="submit" className="cont-btn" disabled={sending}>
                                    {sending ? '送信中...' : '認証コードを送信する'}
                                </button>
                            </form>
                        ) : (
                            <div>
                                <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: 20, lineHeight: 1.7 }}>
                                    <strong style={{ color: '#1a1a2e' }}>{loginEmail}</strong><br />
                                    に6桁のコードを送信しました。
                                </p>
                                <form onSubmit={handleVerifyOtp}>
                                    <div style={{ marginBottom: 20 }}>
                                        <label style={LBL}>認証コード</label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            maxLength={6}
                                            value={loginCode}
                                            onChange={e => setLoginCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                            onFocus={() => setFocusedField('code')} onBlur={() => setFocusedField(null)}
                                            placeholder="000000"
                                            autoFocus
                                            style={{
                                                ...inp('code'),
                                                fontSize: '2rem',
                                                letterSpacing: '0.5em',
                                                textAlign: 'center',
                                                fontFamily: 'monospace',
                                                padding: '16px 14px',
                                            }}
                                        />
                                    </div>
                                    {loginError && <p style={{ color: '#e53935', fontSize: '0.85rem', marginBottom: 12 }}>{loginError}</p>}
                                    <button type="submit" className="cont-btn" disabled={loginCode.length < 6}>
                                        ログイン
                                    </button>
                                </form>
                                <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 16, fontSize: '0.83rem' }}>
                                    {cooldown > 0 ? (
                                        <span style={{ color: '#9ca3af' }}>{cooldown}秒後に再送できます</span>
                                    ) : (
                                        <button onClick={handleResendOtp} disabled={sending} style={{ background: 'none', border: 'none', color: '#005c53', cursor: 'pointer', fontWeight: 600, fontSize: '0.83rem', padding: 0 }}>
                                            コードを再送する
                                        </button>
                                    )}
                                    <span style={{ color: '#e0e4ea' }}>|</span>
                                    <button onClick={() => { setLoginStep(1); setLoginCode(''); setLoginError(null); }} style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '0.83rem', padding: 0 }}>
                                        メールを変更する
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* ── 新規登録フォーム ── */}
                {mode === 'register' && (
                    <div className="form-reg-c">
                        {regDone ? (
                            <div style={{ textAlign: 'center', padding: '24px 0' }}>
                                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
                                    <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="32" cy="32" r="32" fill="#005c53" fillOpacity="0.08" />
                                        <circle cx="32" cy="32" r="24" fill="#005c53" fillOpacity="0.12" />
                                        <circle cx="32" cy="32" r="16" fill="#005c53" />
                                        <polyline points="23,32 29,38 41,26" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                                    </svg>
                                </div>
                                <h3 style={{ fontSize: '1.2rem', fontWeight: 700, color: '#005c53', marginBottom: 12 }}>
                                    申請を受け付けました
                                </h3>
                                <p style={{ color: '#6b7280', fontSize: '0.875rem', lineHeight: 1.8, marginBottom: 24 }}>
                                    管理者が審査後、ご登録のメールアドレスへご連絡します。<br />
                                    通常1〜3営業日以内にご連絡します。
                                </p>
                                <Link to="/" style={{ display: 'inline-block', padding: '10px 24px', background: '#141c3d', color: '#fff', borderRadius: 8, fontSize: '0.875rem', fontWeight: 600, textDecoration: 'none' }}>
                                    トップページへ
                                </Link>
                            </div>
                        ) : (
                            <form onSubmit={handleRegister} noValidate>
                                {regErrors._global && (
                                    <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 6, padding: '10px 14px', marginBottom: 16, color: '#b91c1c', fontSize: '0.85rem' }}>
                                        {regErrors._global}
                                    </div>
                                )}

                                <div style={{ marginBottom: 14 }}>
                                    <label style={LBL}>会社名 <span style={{ color: '#ef4444' }}>*</span></label>
                                    <input
                                        style={inp('company_name')} value={form.company_name}
                                        onChange={set('company_name')} placeholder="〇〇建設株式会社"
                                        onFocus={() => setFocusedField('company_name')} onBlur={() => setFocusedField(null)}
                                    />
                                    <ErrMsg field="company_name" />
                                </div>

                                <div style={{ marginBottom: 14 }}>
                                    <label style={LBL}>代表者名 <span style={{ color: '#ef4444' }}>*</span></label>
                                    <input
                                        style={inp('representative')} value={form.representative}
                                        onChange={set('representative')} placeholder="山田 太郎"
                                        onFocus={() => setFocusedField('representative')} onBlur={() => setFocusedField(null)}
                                    />
                                    <ErrMsg field="representative" />
                                </div>

                                <div style={{ marginBottom: 14 }}>
                                    <label style={LBL}>電話番号 <span style={{ color: '#ef4444' }}>*</span></label>
                                    <input
                                        type="tel" style={inp('phone')} value={form.phone}
                                        onChange={e => setForm(f => ({ ...f, phone: e.target.value.replace(/-/g, '') }))}
                                        placeholder="0751234567（ハイフン不要）"
                                        onFocus={() => setFocusedField('phone')} onBlur={() => setFocusedField(null)}
                                    />
                                    <ErrMsg field="phone" />
                                </div>

                                <div style={{ marginBottom: 14 }}>
                                    <label style={LBL}>建設業許可番号 <span style={{ color: '#6b7280', fontSize: '0.78rem', fontWeight: 400 }}>（任意）</span></label>
                                    <input
                                        style={inp('construction_license_number')} value={form.construction_license_number}
                                        onChange={set('construction_license_number')} placeholder="◯◯県知事許可（般-◯）第◯◯◯◯◯号"
                                        onFocus={() => setFocusedField('construction_license_number')} onBlur={() => setFocusedField(null)}
                                    />
                                    <ErrMsg field="construction_license_number" />
                                </div>

                                <div style={{ marginBottom: 14 }}>
                                    <label style={LBL}>メールアドレス <span style={{ color: '#ef4444' }}>*</span></label>
                                    <input
                                        type="email" style={inp('reg_email')} value={form.email}
                                        onChange={set('email')} placeholder="info@example.com"
                                        onFocus={() => setFocusedField('reg_email')} onBlur={() => setFocusedField(null)}
                                    />
                                    <ErrMsg field="email" />
                                </div>

                                <div style={{ background: '#f0fffe', border: '1px solid #b2e8e4', borderRadius: 6, padding: '10px 14px', marginBottom: 20, fontSize: '0.8rem', color: '#005c53', lineHeight: 1.7 }}>
                                    ※ 申請後、管理者による審査（1〜3営業日）があります。<br />
                                    ※ 承認後にログインが可能になります。<br />
                                    ※ ご成約時、工事代金の<strong>20%</strong>を手数料として申し受けます（精算時に差し引いてお振込みします）。
                                </div>

                                <button type="submit" className="cont-btn" disabled={submitting}>
                                    {submitting ? '送信中...' : '登録申請を送信する'}
                                </button>
                            </form>
                        )}
                    </div>
                )}

                {/* 顧客へのリンク */}
                <div style={{ borderTop: '1px solid #e0e4ea', marginTop: 24, paddingTop: 18, textAlign: 'center', fontSize: '0.85rem', color: '#6b7280' }}>
                    顧客の方はこちら{' '}
                    <Link to="/customer/login" style={{ color: '#005c53', fontWeight: 600, textDecoration: 'none' }}>
                        顧客ログイン
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default ContractorLoginPage;
